import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { sellerRegister, getCities } from '../marketApi';

const STEPS = [
  { icon: '📝', title: 'Register', desc: 'Fill in your business details below' },
  { icon: '✅', title: 'Get Approved', desc: 'Admin reviews and approves your account' },
  { icon: '📦', title: 'List Products', desc: 'Add your items with price & stock' },
  { icon: '🛒', title: 'Get Orders', desc: 'Buyers order COD — you deliver locally' },
];

export default function SellerRegister() {
  const [cities, setCities] = useState([]);
  const [otherCity, setOtherCity] = useState('');
  const [form, setForm] = useState({
    businessName: '',
    ownerName: '',
    phone: '',
    whatsapp: '',
    email: '',
    city: '',
    password: '',
    confirmPassword: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    getCities()
      .then(setCities)
      .catch(() => {});
  }, []);

  function handleChange(e) {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  }

  function validate() {
    if (!form.businessName.trim()) return 'Business name is required';
    if (!form.ownerName.trim()) return 'Owner name is required';
    if (!/^\d{10}$/.test(form.phone.trim())) return 'Please enter a valid 10-digit phone number';
    if (form.whatsapp && !/^\d{10}$/.test(form.whatsapp.trim()))
      return 'WhatsApp number must be 10 digits';
    const effectiveCity = form.city === '__other' ? otherCity.trim() : form.city.trim();
    if (!effectiveCity) return 'City is required';
    if (form.password.length < 6) return 'Password must be at least 6 characters';
    if (form.password !== form.confirmPassword) return 'Passwords do not match';
    return null;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const err = validate();
    if (err) { setError(err); return; }
    setError('');
    setLoading(true);
    try {
      await sellerRegister({
        businessName: form.businessName.trim(),
        ownerName: form.ownerName.trim(),
        phone: form.phone.trim(),
        whatsapp: form.whatsapp.trim() || form.phone.trim(),
        email: form.email.trim(),
        city: form.city === '__other' ? otherCity.trim() : form.city.trim(),
        password: form.password,
      });
      setSuccess(true);
    } catch (e) {
      setError(e.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  if (success) {
    return (
      <div className="page-container" style={{ maxWidth: 480, margin: '40px auto' }}>
        <div className="card" style={{ padding: 32, textAlign: 'center' }}>
          <div style={{ fontSize: 56, marginBottom: 16 }}>🎉</div>
          <h2 style={{ fontWeight: 800, marginBottom: 10 }}>Registration Submitted!</h2>
          <p style={{ color: 'var(--muted)', lineHeight: 1.6, marginBottom: 8 }}>
            Thank you for registering on SmaBizZ Market!
          </p>
          <p style={{ color: 'var(--muted)', lineHeight: 1.6, marginBottom: 24 }}>
            Our admin team will review your account and approve it shortly.
            You'll be able to log in and add your products once approved.
          </p>
          <div
            style={{
              background: 'var(--bg)',
              borderRadius: 12,
              padding: '16px 20px',
              marginBottom: 24,
              textAlign: 'left',
            }}
          >
            <div style={{ fontWeight: 700, marginBottom: 8, fontSize: 13 }}>What happens next?</div>
            {['Admin reviews your registration (usually within 24 hours)',
              'You receive approval — log in with your phone + password',
              'Add your products under "My Products" tab',
              'Buyers in your city can order from you'].map((s, i) => (
              <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 6, fontSize: 13 }}>
                <span style={{ color: 'var(--success)', fontWeight: 700, minWidth: 16 }}>{i + 1}.</span>
                <span style={{ color: 'var(--muted)' }}>{s}</span>
              </div>
            ))}
          </div>
          <Link to="/seller/login" className="btn btn-primary btn-full">
            Go to Seller Login
          </Link>
          <Link to="/" style={{ display: 'block', marginTop: 12, color: 'var(--muted)', fontSize: 13 }}>
            Back to Marketplace
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container" style={{ maxWidth: 520, margin: '24px auto' }}>

      {/* How it works */}
      <div className="mk-steps-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginBottom: 24 }}>
        {STEPS.map((s, i) => (
          <div
            key={i}
            className="card"
            style={{ padding: '12px 8px', textAlign: 'center' }}
          >
            <div style={{ fontSize: 24, marginBottom: 4 }}>{s.icon}</div>
            <div style={{ fontWeight: 700, fontSize: 12 }}>{s.title}</div>
            <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2, lineHeight: 1.3 }}>{s.desc}</div>
          </div>
        ))}
      </div>

      <div className="card" style={{ padding: 28 }}>
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <div style={{ fontSize: 36, marginBottom: 8 }}>🏪</div>
          <h2 style={{ fontWeight: 800, fontSize: 22 }}>Seller Registration</h2>
          <p style={{ color: 'var(--muted)', fontSize: 13, marginTop: 4 }}>
            Start selling on SmaBizZ Market — Step 1 of 4
          </p>
        </div>

        {error && <div className="alert alert-error">{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Business / Shop Name *</label>
            <input
              className="form-control"
              name="businessName"
              value={form.businessName}
              onChange={handleChange}
              placeholder="e.g. Sri Lakshmi Vegetables"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Owner Name *</label>
            <input
              className="form-control"
              name="ownerName"
              value={form.ownerName}
              onChange={handleChange}
              placeholder="Your full name"
            />
          </div>

          <div className="two-col">
            <div className="form-group">
              <label className="form-label">Mobile Number * (login ID)</label>
              <input
                className="form-control"
                name="phone"
                value={form.phone}
                onChange={handleChange}
                placeholder="10-digit number"
                inputMode="numeric"
                maxLength={10}
              />
            </div>
            <div className="form-group">
              <label className="form-label">WhatsApp Number</label>
              <input
                className="form-control"
                name="whatsapp"
                value={form.whatsapp}
                onChange={handleChange}
                placeholder="Same as mobile if blank"
                inputMode="numeric"
                maxLength={10}
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Email (optional)</label>
            <input
              className="form-control"
              name="email"
              type="email"
              value={form.email}
              onChange={handleChange}
              placeholder="your@email.com"
            />
          </div>

          <div className="form-group">
            <label className="form-label">City * (where you deliver)</label>
            {cities.length > 0 ? (
              <select
                className="form-control"
                name="city"
                value={form.city}
                onChange={handleChange}
              >
                <option value="">Select your city</option>
                {cities.map((c) => (
                  <option key={c.city || c} value={c.city || c}>
                    {c.city || c}
                  </option>
                ))}
                <option value="__other">Other city (type below)</option>
              </select>
            ) : (
              <input
                className="form-control"
                name="city"
                value={form.city}
                onChange={handleChange}
                placeholder="Your city"
              />
            )}
            {cities.length > 0 && form.city === '__other' && (
              <input
                className="form-control"
                value={otherCity}
                onChange={e => setOtherCity(e.target.value)}
                placeholder="Type your city name"
                style={{ marginTop: 8 }}
                autoFocus
              />
            )}
            {cities.length > 0 && (
              <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>
                Your products will be visible to buyers in this city
              </div>
            )}
          </div>

          <div className="two-col">
            <div className="form-group">
              <label className="form-label">Password *</label>
              <input
                className="form-control"
                name="password"
                type="password"
                value={form.password}
                onChange={handleChange}
                placeholder="Min 6 characters"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Confirm Password *</label>
              <input
                className="form-control"
                name="confirmPassword"
                type="password"
                value={form.confirmPassword}
                onChange={handleChange}
                placeholder="Repeat password"
              />
            </div>
          </div>

          <button
            type="submit"
            className="btn btn-primary btn-full btn-lg"
            disabled={loading}
            style={{ marginTop: 8 }}
          >
            {loading ? 'Registering...' : 'Submit Registration'}
          </button>
        </form>

        <div style={{ textAlign: 'center', marginTop: 16, fontSize: 13 }}>
          Already registered?{' '}
          <Link to="/seller/login" style={{ fontWeight: 600 }}>
            Login here
          </Link>
        </div>
        <div style={{ textAlign: 'center', marginTop: 8 }}>
          <Link to="/" style={{ fontSize: 13, color: 'var(--muted)' }}>
            ← Back to Marketplace
          </Link>
        </div>
      </div>
    </div>
  );
}
